import { useEffect, useRef, useState } from 'react';
import { ImageUp, Loader2, Trash2 } from 'lucide-react';
import type { EmpresaPerfilResponse } from '@triade/contracts';
import { LOGO_MAX_BYTES } from '@triade/contracts';
import { useEnviarLogo, useLogoUrl, useRemoverLogo } from '@/api/empresa';
import { ApiError } from '@/lib/api';
import { problemaNaLogo } from './tipos';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

const BOTAO = 'inline-flex h-9 items-center gap-2 rounded-[10px] border border-border bg-card px-3 font-medium hover:bg-line-soft disabled:opacity-50';

/**
 * Logo da empresa no PDF. Escolher um arquivo mostra a pre-visualizacao local; "Usar esta logo" envia (PUT /empresa/logo,
 * multipart) e o servidor decide pelos magic bytes e pelo limite de 512 KB. A logo gravada vem de GET /empresa/logo.
 */
export function LogoEmpresa({ perfil, editavel }: { perfil: EmpresaPerfilResponse; editavel: boolean }) {
  const gravada = useLogoUrl(perfil.temLogo, perfil.atualizadoEm);
  const enviar = useEnviarLogo();
  const remover = useRemoverLogo();
  const entrada = useRef<HTMLInputElement>(null);
  const [escolhido, setEscolhido] = useState<File | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!escolhido) {
      setPrevia(null);
      return;
    }
    const u = URL.createObjectURL(escolhido);
    setPrevia(u);
    return () => URL.revokeObjectURL(u);
  }, [escolhido]);

  function escolher(arquivo: File | undefined) {
    setErro(null);
    if (!arquivo) return;
    const problema = problemaNaLogo(arquivo);
    if (problema) {
      setEscolhido(null);
      setErro(problema);
      return;
    }
    setEscolhido(arquivo);
  }

  function limpar() {
    setEscolhido(null);
    if (entrada.current) entrada.current.value = '';
  }

  async function usar() {
    if (!escolhido) return;
    setErro(null);
    try {
      await enviar.mutateAsync(escolhido);
      limpar();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  async function tirar() {
    setErro(null);
    try {
      await remover.mutateAsync();
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  const exibida = previa ?? gravada.url;
  return (
    <section aria-label="Logo da empresa" className="rounded-2xl border border-border bg-card p-5">
      <h2 className="text-title font-semibold">Logo</h2>
      <p className="mt-1 text-small text-ink-secondary">Aparece no topo do PDF do orçamento. PNG ou JPEG de até {LOGO_MAX_BYTES / 1024} KB (SVG não é aceito).</p>
      <div className="mt-4 flex flex-wrap items-center gap-5">
        <div
          data-testid="logo-previa"
          className="flex h-[88px] w-[200px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-surface-muted"
        >
          {exibida ? (
            <img src={exibida} alt={previa ? 'Pré-visualização da nova logo' : 'Logo atual da empresa'} className="max-h-full max-w-full object-contain" />
          ) : gravada.carregando ? (
            <span role="status" className="text-caption text-ink-muted">
              Carregando logo…
            </span>
          ) : (
            <span className="text-caption text-ink-muted">Sem logo</span>
          )}
        </div>
        {editavel && (
          <div className="flex flex-col gap-2">
            <input
              ref={entrada}
              id="logo-arquivo"
              data-testid="logo-arquivo"
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              onChange={(e) => escolher(e.target.files?.[0])}
            />
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="logo-arquivo" className={`${BOTAO} cursor-pointer`}>
                <ImageUp aria-hidden="true" className="size-4" />
                {perfil.temLogo || escolhido ? 'Trocar arquivo…' : 'Escolher arquivo…'}
              </label>
              {escolhido && (
                <>
                  <button type="button" onClick={() => void usar()} disabled={enviar.isPending} className="inline-flex h-9 items-center gap-2 rounded-[10px] bg-primary px-3.5 font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                    {enviar.isPending && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
                    Usar esta logo
                  </button>
                  <button type="button" onClick={limpar} disabled={enviar.isPending} className={BOTAO}>
                    Cancelar
                  </button>
                </>
              )}
              {perfil.temLogo && !escolhido && (
                <button type="button" onClick={() => void tirar()} disabled={remover.isPending} className={BOTAO}>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Remover logo
                </button>
              )}
            </div>
            {escolhido && (
              <p className="text-caption text-ink-muted">
                {escolhido.name} · {Math.max(1, Math.round(escolhido.size / 1024))} KB. Ainda não foi enviada.
              </p>
            )}
          </div>
        )}
      </div>
      {erro && (
        <p role="alert" className="mt-3 text-small text-danger-ink">
          {erro}
        </p>
      )}
    </section>
  );
}
